import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";

import {
  ensureFirebaseAuth,
  firebaseDb,
  firebaseStorage,
  isFirebaseConfigured,
  tryEnsureFirebaseAuth,
} from "data/firebase";
import { normalizeAd } from "data/admin/domain";
import { seedAds } from "data/seedAds";

const COLLECTION = "ads";

function requireDb() {
  if (!isFirebaseConfigured || !firebaseDb) {
    throw new Error("Firebase yapılandırılmamış.");
  }
  return firebaseDb;
}

function decorate(raw, id) {
  return normalizeAd({ ...(raw || {}), id: id || raw?.id });
}

export async function listAdsFromFirestore({ includeDeleted = false } = {}) {
  const db = requireDb();
  await tryEnsureFirebaseAuth();
  const snap = await getDocs(collection(db, COLLECTION));
  let items = snap.docs.map((d) => decorate(d.data(), d.id));
  if (!includeDeleted) {
    items = items.filter((item) => !item.deletedAt && item.isActive !== false);
  }
  return items.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || a.title.localeCompare(b.title));
}

export async function getAdFromFirestore(id) {
  const db = requireDb();
  await tryEnsureFirebaseAuth();
  const snap = await getDoc(doc(db, COLLECTION, id));
  if (!snap.exists()) return null;
  return decorate(snap.data(), snap.id);
}

export async function saveAdToFirestore(ad) {
  const db = requireDb();
  await ensureFirebaseAuth();
  const now = new Date().toISOString();
  const next = decorate({
    ...ad,
    createdAt: ad.createdAt || now,
    updatedAt: now,
  });
  await setDoc(doc(db, COLLECTION, next.id), next, { merge: true });
  return next;
}

export async function softDeleteAdInFirestore(id) {
  const db = requireDb();
  await ensureFirebaseAuth();
  await updateDoc(doc(db, COLLECTION, id), {
    deletedAt: new Date().toISOString(),
    isActive: false,
    updatedAt: new Date().toISOString(),
  });
}

export async function restoreAdInFirestore(id) {
  const db = requireDb();
  await ensureFirebaseAuth();
  await updateDoc(doc(db, COLLECTION, id), {
    deletedAt: null,
    isActive: true,
    updatedAt: new Date().toISOString(),
  });
}

export async function hardDeleteAdInFirestore(id) {
  const db = requireDb();
  await ensureFirebaseAuth();
  await deleteDoc(doc(db, COLLECTION, id));
}

/** Varsayılan reklamları Firestore'a yazar. force=true ise üzerine yazar. */
export async function seedAdsToFirestore({ force = false } = {}) {
  const existing = await listAdsFromFirestore({ includeDeleted: true });
  if (existing.length && !force) {
    return { seeded: false, count: existing.length };
  }
  await ensureFirebaseAuth();
  const now = new Date().toISOString();
  for (const ad of seedAds) {
    await saveAdToFirestore({
      ...ad,
      createdAt: ad.createdAt || now,
      updatedAt: now,
      deletedAt: null,
      isActive: true,
    });
  }
  return { seeded: true, count: seedAds.length, forced: force };
}

export async function seedAdsToFirestoreIfEmpty() {
  return seedAdsToFirestore({ force: false });
}

export async function uploadAdImage(file, adId = "draft") {
  if (!firebaseStorage) {
    throw new Error("Firebase Storage yapılandırılmamış.");
  }
  if (!file) {
    throw new Error("Dosya seçilmedi.");
  }
  await ensureFirebaseAuth();

  const safeName = String(file.name || "banner.jpg").replace(/[^\w.-]+/g, "_");
  const path = `ads/${adId}/${Date.now()}-${safeName}`;
  const storageRef = ref(firebaseStorage, path);
  await uploadBytes(storageRef, file, {
    contentType: file.type || "image/jpeg",
  });
  return getDownloadURL(storageRef);
}
